import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';

const stats = [
  { value: '10,000+', label: '日韩大片资源' },
  { value: '500万+', label: '月活跃用户' },
  { value: '99.8%', label: '用户好评率' },
  { value: '365天', label: '全年无休更新' },
];

const timeline = [
  {
    year: '2020',
    title: '平台成立',
    description: '日韩大片在线观看平台正式上线，专注为华语用户提供优质的日韩电影观看体验。',
  },
  {
    year: '2021',
    title: '内容扩展',
    description: '新增动漫剧场版和日韩剧场版分类，资源库扩充至5000部以上。',
  },
  {
    year: '2022',
    title: '体验升级',
    description: '全面升级播放画质，支持超清资源，优化推荐算法，提供更精准的电影推荐。',
  },
  {
    year: '2023',
    title: '用户突破',
    description: '月活跃用户突破500万，成为华语地区最受欢迎的日韩电影平台之一。',
  },
  {
    year: '2024',
    title: '持续创新',
    description: '引入AI智能推荐系统，每日更新最新日韩大片资源，持续优化用户体验。',
  },
];

export default function AboutPage() {
  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 日韩大片在线观看',
    description: '日韩大片在线观看平台品牌介绍，了解我们的发展历程、核心优势和联系方式。',
    url: 'https://rihan-dapian.com/about',
    mainEntity: {
      '@type': 'Organization',
      name: '日韩大片在线观看',
      url: 'https://rihan-dapian.com',
      description: '最新最全日韩大片在线观看平台，涵盖日本电影、韩国电影、日韩剧场版等精品内容。',
      foundingDate: '2020',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+86-400-888-9999',
        contactType: 'customer service',
        email: 'contact@rihan-dapian.com',
        availableLanguage: ['Chinese'],
      },
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '日韩大片在线观看平台是否免费？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，日韩大片平台完全免费，所有电影资源均可在线观看，无需注册或付费。',
        },
      },
      {
        '@type': 'Question',
        name: '日韩大片平台的电影资源是否正版？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们致力于与版权方合作，为用户提供正版、高清的日韩电影资源。平台所有内容均来自合法渠道。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系日韩大片平台客服？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过以下方式联系我们：客服电话 400-888-9999，邮箱 contact@rihan-dapian.com，或访问我们的在线客服页面。',
        },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Page Header */}
          <section className="bg-background-100 py-10 md:py-14">
            <div className="max-w-5xl mx-auto px-4 md:px-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mt-4 mb-3">
                关于我们
              </h1>
              <p className="text-foreground-600 max-w-2xl">
                了解日韩大片在线观看平台的故事、使命与愿景。
              </p>
            </div>
          </section>

          {/* Brand Introduction */}
          <section className="py-10 md:py-14 bg-background-50">
            <div className="max-w-5xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">品牌故事</h2>
                  <div className="space-y-4 text-foreground-600 leading-relaxed">
                    <p>
                      日韩大片在线观看平台成立于2020年，我们的使命是让每一位华语观众都能便捷地欣赏到最优质的<strong>日韩大片</strong>。从日本电影的细腻情感到韩国电影的大胆创新，我们致力于打造最全面的<strong>日韩高清电影</strong>资源库。
                    </p>
                    <p>
                      我们的团队由一群热爱日本和韩国电影的资深影迷组成。我们深知影迷对<strong>最新日韩大片2024</strong>的期待，因此坚持每日更新，确保用户第一时间看到最新的<strong>韩国大片推荐</strong>和<strong>日本大片排行榜</strong>热门内容。
                    </p>
                    <p>
                      无论是新海诚的唯美动画、奉俊昊的社会寓言，还是马东锡的硬汉爽片，我们的<strong>日韩大片免费播放</strong>平台都为您准备好了高清观影体验。
                    </p>
                  </div>
                </div>
                <div className="w-full h-72 md:h-80 rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20office%20team%20working%20on%20film%20and%20cinema%20content%2C%20creative%20workspace%20with%20movie%20posters%20on%20walls%2C%20warm%20lighting%2C%20collaborative%20atmosphere%2C%20professional%20yet%20casual%20environment&width=800&height=600&seq=about-brand-story&orientation=landscape"
                    alt="日韩大片团队"
                    title="日韩大片在线观看 - 品牌故事"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-5xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                {stats.map((stat) => (
                  <div key={stat.label} className="text-center p-5 md:p-6 rounded-2xl bg-background-50 border border-background-200/70">
                    <p className="font-heading text-2xl md:text-3xl font-bold text-primary-500 mb-1">{stat.value}</p>
                    <p className="text-sm text-foreground-500">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Development Timeline */}
          <section className="py-10 md:py-14 bg-background-50">
            <div className="max-w-5xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-8 text-center">发展历程</h2>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 md:-translate-x-px"></div>
                <div className="space-y-8">
                  {timeline.map((item, index) => (
                    <div key={item.year} className={`relative flex flex-col md:flex-row gap-4 md:gap-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                      <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'} pl-10 md:pl-0`}>
                        <div className="hidden md:block">
                          <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 rounded-full text-sm font-bold whitespace-nowrap mb-2">
                            {item.year}
                          </span>
                          <h3 className="font-heading font-semibold text-lg text-foreground-900 mb-1">{item.title}</h3>
                          <p className="text-sm text-foreground-500">{item.description}</p>
                        </div>
                        <div className="md:hidden">
                          <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 rounded-full text-sm font-bold whitespace-nowrap mb-2">
                            {item.year}
                          </span>
                          <h3 className="font-heading font-semibold text-lg text-foreground-900 mb-1">{item.title}</h3>
                          <p className="text-sm text-foreground-500">{item.description}</p>
                        </div>
                      </div>
                      <div className="absolute left-[0.4rem] md:left-1/2 top-2 w-3 h-3 rounded-full bg-accent-500 border-2 border-background-50 md:-translate-x-1/2"></div>
                      <div className="flex-1 hidden md:block"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-5xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-8 text-center">联系我们</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
                <div className="p-6 rounded-2xl bg-background-50 border border-background-200/70 text-center">
                  <span className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 mx-auto mb-4">
                    <i className="ri-mail-line text-primary-500 text-xl"></i>
                  </span>
                  <h3 className="font-heading font-semibold text-foreground-900 mb-2">电子邮箱</h3>
                  <p className="text-sm text-foreground-500">contact@rihan-dapian.com</p>
                </div>
                <div className="p-6 rounded-2xl bg-background-50 border border-background-200/70 text-center">
                  <span className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 mx-auto mb-4">
                    <i className="ri-phone-line text-accent-500 text-xl"></i>
                  </span>
                  <h3 className="font-heading font-semibold text-foreground-900 mb-2">客服电话</h3>
                  <p className="text-sm text-foreground-500">400-888-9999</p>
                </div>
                <div className="p-6 rounded-2xl bg-background-50 border border-background-200/70 text-center">
                  <span className="w-12 h-12 flex items-center justify-center rounded-xl bg-secondary-100 mx-auto mb-4">
                    <i className="ri-map-pin-line text-secondary-500 text-xl"></i>
                  </span>
                  <h3 className="font-heading font-semibold text-foreground-900 mb-2">公司地址</h3>
                  <p className="text-sm text-foreground-500">北京市朝阳区建国路88号</p>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}